import { SYSTEM_DEFAULT_DEVICE_MARKER } from '@/tauri/constants';
import { logger } from '@/utils/logger/logger';
import { isTauri } from '@heidi/runtime/platform';

/**
 * Synthetic UI select value meaning "use the same mic as session recording".
 * Never persisted — maps to `undefined` in
 * `tauriDictationInputDevicePreferenceAtom`.
 */
export const SAME_AS_SESSION_MICROPHONE = '__SESSION_MICROPHONE__';

/**
 * Synthetic UI select value while the saved Dictate mic is unplugged. It
 * matches no option, so picking any option (including "Same as session
 * microphone") still fires `onValueChange` — Radix skips same-value picks.
 */
export const UNAVAILABLE_DICTATION_MICROPHONE = '__UNAVAILABLE_MICROPHONE__';

/** Cross-window snapshot broadcast for the Dictate and session microphones. */
export const DICTATION_INPUT_DEVICE_PREFERENCE_CHANGED_EVENT =
  'dictation:input-device-preference-changed';
/** Sent by a secondary webview when it needs the current main-window snapshot. */
export const DICTATION_INPUT_DEVICE_PREFERENCE_REQUEST_EVENT =
  'dictation:input-device-preference-request';

export type DictationInputDevicePreferenceChangedPayload = {
  /** Stored preference, or `null` for session inheritance. */
  preference: string | null;
  /** Main-window session mic, or `null` when it tracks the OS default. */
  sessionDeviceName?: string | null;
};

/**
 * Resolve the native Dictate input device name for `setDictationInputDevice`.
 *
 * - `undefined` preference → inherit session mic (`SYSTEM_DEFAULT_DEVICE_MARKER`
 *   / missing session → `null` so Rust tracks the OS default)
 * - `SYSTEM_DEFAULT_DEVICE_MARKER` preference → explicit OS default (`null`)
 * - any other string → physical CPAL device name
 */
export function resolveDictationInputDeviceName(
  preference: string | undefined,
  sessionDeviceName: string | undefined
): string | null {
  if (preference === undefined) {
    if (!sessionDeviceName || sessionDeviceName === SYSTEM_DEFAULT_DEVICE_MARKER) {
      return null;
    }
    return sessionDeviceName;
  }
  if (preference === SYSTEM_DEFAULT_DEVICE_MARKER) {
    return null;
  }
  return preference;
}

export type DictationMicrophoneOption = { value: string; label: string };

/**
 * A saved device that is not connected presents as the session mic, which is
 * what native records from. The stored preference is left intact so a replug
 * restores it.
 */
export function buildDictationMicrophonePresentation({
  preference,
  selectValue,
  resolvedDeviceName,
  sessionDeviceName,
  sameAsSessionLabel,
  availableOptions,
}: {
  preference: string | undefined;
  selectValue: string;
  resolvedDeviceName: string | null;
  sessionDeviceName: string | undefined;
  sameAsSessionLabel: string;
  availableOptions: DictationMicrophoneOption[];
}) {
  const isPreferredDeviceMissing =
    preference !== undefined &&
    preference !== SYSTEM_DEFAULT_DEVICE_MARKER &&
    availableOptions.every(({ value }) => value !== preference);
  const options = [
    { value: SAME_AS_SESSION_MICROPHONE, label: sameAsSessionLabel },
    ...availableOptions,
  ];
  const effectiveSelectValue = isPreferredDeviceMissing
    ? UNAVAILABLE_DICTATION_MICROPHONE
    : selectValue;
  const effectiveDeviceName = isPreferredDeviceMissing
    ? resolveDictationInputDeviceName(undefined, sessionDeviceName)
    : resolvedDeviceName;

  return {
    options,
    selectValue: effectiveSelectValue,
    selectedLabel:
      options.find(({ value }) => value === effectiveSelectValue)?.label ??
      sameAsSessionLabel,
    meterDeviceName: effectiveDeviceName ?? SYSTEM_DEFAULT_DEVICE_MARKER,
  };
}

/** Map storage preference → UI select value (synthetic same-as-session). */
export function toDictationMicrophoneSelectValue(preference: string | undefined): string {
  return preference === undefined ? SAME_AS_SESSION_MICROPHONE : preference;
}

/**
 * Normalize a setter argument that may be the synthetic UI value, `undefined`
 * (inherit), the system-default marker, or a CPAL device name.
 */
export function normalizeDictationMicrophonePreference(
  next: string | undefined
): string | undefined {
  if (next === undefined || next === SAME_AS_SESSION_MICROPHONE) return undefined;
  return next;
}

/** Broadcast the current microphone snapshot to every Tauri webview. */
export async function emitDictationInputDevicePreferenceChanged(
  preference: string | null,
  sessionDeviceName?: string | null
): Promise<void> {
  if (!isTauri()) return;
  try {
    // eslint-disable-next-line @heidi/rules/no-new-direct-tauri-import -- relocated unchanged; dynamic event import keeps the SDK off the web bundle and plugin-core has no emit/listen helper with these semantics
    const { emit } = await import('@tauri-apps/api/event');
    const payload: DictationInputDevicePreferenceChangedPayload = {
      preference,
      sessionDeviceName,
    };
    await emit(DICTATION_INPUT_DEVICE_PREFERENCE_CHANGED_EVENT, payload);
  } catch (error) {
    logger.warn('[dictation/mic-preference] failed to emit preference change', {
      errorName: error instanceof Error ? error.name : typeof error,
    });
  }
}

/** Ask the main window to broadcast its current microphone snapshot. */
export async function requestDictationInputDevicePreference(): Promise<void> {
  if (!isTauri()) return;
  try {
    // eslint-disable-next-line @heidi/rules/no-new-direct-tauri-import -- relocated unchanged; dynamic event import keeps the SDK off the web bundle and plugin-core has no emit/listen helper with these semantics
    const { emit } = await import('@tauri-apps/api/event');
    await emit(DICTATION_INPUT_DEVICE_PREFERENCE_REQUEST_EVENT);
  } catch (error) {
    logger.warn('[dictation/mic-preference] failed to request preference state', {
      errorName: error instanceof Error ? error.name : typeof error,
    });
  }
}
